import { Popover, PopoverContent, PopoverTrigger } from '@/_components/ui'
import { cn } from '@/_lib/utils'
import { env } from '@/env'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { Building2, LogOut, PanelLeftOpen, ShieldAlert } from 'lucide-react'
import { Fragment } from 'react'
import { SlArrowRight } from 'react-icons/sl'

import { useSession } from '../_hooks/use-session'
import { authClient } from '../_lib/auth-client'
import { useBreadcrumbStore, useCompanyStore, useSidebarStore } from '../_stores'

const ROUTE_LABELS: Record<string, string> = {
  '/companies': 'Empresas',
  '/applications': 'Pacientes',
  '/simulation': 'Simulação',
}

function getFallbackLabel(pathname: string): string {
  for (const [route, label] of Object.entries(ROUTE_LABELS)) {
    if (pathname.startsWith(route)) return label
  }
  return 'Dashboard'
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export function DashboardHeader() {
  const { data: session } = useSession()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const company = useCompanyStore((s) => s.company)
  const breadcrumbItems = useBreadcrumbStore((s) => s.items)
  const { isCollapsed, toggle } = useSidebarStore()

  const userName = session?.user.name ?? ''
  const initials = userName ? getInitials(userName) : ''

  const handleSignOutClick = async () => {
    await authClient.signOut()
    window.location.href = `${env.VITE_DOMAIN_URL}/login`
  }

  return (
    <header
      className={cn(
        'flex h-14 shrink-0 items-center justify-between border-b border-brand-grayscale-4 bg-brand-grayscale-1 px-6',
        isCollapsed ? 'pl-3' : 'pl-14',
      )}
    >
      <div className="flex items-center gap-3">
        {isCollapsed && (
          <button
            aria-label="Expandir menu"
            className="shrink-0 rounded-md p-1.5 text-brand-grayscale-7 transition-colors hover:bg-brand-grayscale-3"
            onClick={toggle}
          >
            <PanelLeftOpen size={20} />
          </button>
        )}
        {breadcrumbItems.length > 0 ? (
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
            {breadcrumbItems.map((item, i) => (
              <Fragment key={i}>
                {i > 0 && <SlArrowRight size={8} className="text-brand-grayscale-7" />}
                {item.href ? (
                  <a
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault()
                      navigate({ to: item.href })
                    }}
                    className="text-sm text-brand-grayscale-7 hover:underline"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span className="text-sm font-semibold text-brand-green-primary-5">
                    {item.label}
                  </span>
                )}
              </Fragment>
            ))}
          </nav>
        ) : (
          <span className="text-sm text-brand-grayscale-7">{getFallbackLabel(pathname)}</span>
        )}
      </div>

      <div className="flex items-center gap-4">
        {session?.user.userType === 'staff' ? (
          <div className="flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-50 px-2 py-1">
            <ShieldAlert className="text-amber-600" size={14} />
            <span className="text-xs font-medium text-amber-700">Administrador</span>
          </div>
        ) : (
          company && (
            <div className="flex items-center gap-1.5 rounded-full border border-brand-grayscale-4 px-3 py-1">
              <Building2 className="text-brand-grayscale-6" size={14} />
              <span className="text-xs font-medium text-brand-grayscale-7">{company.name}</span>
            </div>
          )
        )}
        <div className="flex items-center gap-3">
          <span className="text-sm text-brand-grayscale-7">{userName}</span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                aria-label="Abrir menu do usuário"
                className="flex h-8 w-8 select-none items-center justify-center rounded-full bg-brand-green-primary-5 text-xs font-semibold text-white"
              >
                {initials}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-32 p-1">
              <button
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-brand-grayscale-7 transition-colors hover:bg-brand-grayscale-3"
                onClick={handleSignOutClick}
              >
                <LogOut size={16} />
                Sair
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </header>
  )
}
